// TraitementConges.jsx (RH/Admin) — Redesigné avec le style TraitementAvances

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    AlertCircle,
    FileText,
    ChevronDown,
    CalendarDays,
    Loader,
    Eye,
    Download,
} from "lucide-react";
import { API_BASE_URL } from "../../../../../api";
import {
    getAdminInboxRequest,
    adminDecisionRequest,
    clearLeaveRequestMessage,
    exportLeaveRequestsExcelRequest,
} from "../../../../redux/actions/leaveRequestsActions";
import "../TraitementStyles/TraitementNotesFrais.css";

const TraitementConges = () => {
    const dispatch = useDispatch();

    // Redux state
    const leaveState = useSelector((state) => state.leaveRequests || {});
    const loading = !!leaveState.loading;
    const apiError = leaveState.error;
    const inbox = leaveState.adminInbox || [];

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [decisionComment, setDecisionComment] = useState("");
    const [pendingDecision, setPendingDecision] = useState(null); // "approve" | "reject"
    const [previewDoc, setPreviewDoc] = useState(null); // { url, name, path }

    const [filters, setFilters] = useState({
        typeConge: "",
        dateDebut: "",
        dateFin: "",
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminInboxRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr; // retourne tel quel si pas parsable
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const mapStatusToTab = (s) => {
        const v = String(s || "").toUpperCase();
        if (v === "PENDING_ADMIN" || v === "POSTPONEMENT_REQUEST") return "enCours";

        if (v === "APPROVED") return "approuve";
        if (v === "REJECTED") return "rejete";
        if (v === "CANCELLED") return "annule";
        if (v === "POSTPONED") return "reporte";
        return "enCours";
    };

    const demandes = useMemo(() => {
        return (inbox || []).map((d) => ({
            id: d.id,
            matricule: d.employeeCode || "—",
            nom: d.employeeName || "—",
            typeConge: d.absenceTypeNom || "—",
            dateDebut: d.dateDebut,
            dateFin: d.dateFin,
            duree: d.duree ?? 0,
            motif: d.commentaire || " - ",
            justificatif: d.justificatif || null,
            statut: mapStatusToTab(d.status),
            rawStatus: d.status,
            managerDecision: d.managerDecision ?? null,
            managerName: d.managerName || null,
            managerDecisionAt: d.managerDecisionAt || null,
            managerComment: d.managerComment || null,
            adminDecision: d.adminDecision ?? null,
            adminName: d.adminName || null,
            adminDecisionAt: d.adminDecisionAt || null,
            adminComment: d.adminComment || null,
            originalDateDebut: d.originalDateDebut,
            originalDateFin: d.originalDateFin,
            dateDemande: d.createdAt,
            justificatifs: d.justificatifs || [],
        }));
    }, [inbox]);

    const getStatutBadge = (demande) => {
        const raw = String(demande.rawStatus || "").toUpperCase();
        if (raw === "POSTPONEMENT_REQUEST") {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#f0f9ff", color: "#0c4a6e" }}>
                    Report en attente
                </span>
            );
        }
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: { label: "Approuvée", bg: "#d4edda", color: "#155724" },
            rejete: { label: "Rejetée", bg: "#f8d7da", color: "#721c24" },
            annule: { label: "Annulée", bg: "#f3f4f6", color: "#6b7280" },
            reporte: { label: "Reportée", bg: "#e0f2fe", color: "#0369a1" },
        };
        const c = config[demande.statut] || config.enCours;
        return (
            <span className="tcBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };


    const getFilteredDemandes = () => {
        let filtered = demandes.filter((d) => d.statut === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.nom && d.nom.toLowerCase().includes(s)) ||
                    (d.matricule && d.matricule.toLowerCase().includes(s)) ||
                    (d.typeConge && d.typeConge.toLowerCase().includes(s))
            );
        }

        if (filters.typeConge) {
            filtered = filtered.filter((d) => d.typeConge === filters.typeConge);
        }

        if (filters.dateDebut) {
            filtered = filtered.filter((d) => {
                if (!d.dateDebut) return true;
                const date = new Date(d.dateDebut).toISOString().split("T")[0];
                return date >= filters.dateDebut;
            });
        }

        if (filters.dateFin) {
            filtered = filtered.filter((d) => {
                if (!d.dateFin) return true;
                const date = new Date(d.dateFin).toISOString().split("T")[0];
                return date <= filters.dateFin;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        return [
            {
                id: "enCours",
                label: "En cours",
                count: demandes.filter((d) => d.statut === "enCours").length,
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: demandes.filter((d) => d.statut === "approuve").length,
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: demandes.filter((d) => d.statut === "rejete").length,
            },
            {
                id: "annule",
                label: "Annulées",
                count: demandes.filter((d) => d.statut === "annule").length,
            },
            {
                id: "reporte",
                label: "Reportées",
                count: demandes.filter((d) => d.statut === "reporte").length,
            },
        ];
    }, [demandes]);

    const typesConges = useMemo(() => {
        return [...new Set(demandes.map((d) => d.typeConge).filter(Boolean))];
    }, [demandes]);

    // Ouvrir modal de décision (approuver ou rejeter)
    const handleOpenDecision = (demande, decision) => {
        setSelectedDemande(demande);
        setPendingDecision(decision);
        setDecisionComment("");
        setShowDecisionModal(true);
    };

    const confirmDecision = () => {
        if (!selectedDemande) return;
        const approved = pendingDecision === "approve";
        if (!approved && !decisionComment.trim()) return; // motif obligatoire pour rejet

        dispatch(
            adminDecisionRequest({
                id: selectedDemande.id,
                approved,
                comment: decisionComment || "",
            })
        );
        dispatch(clearLeaveRequestMessage());
        closeDecision();
    };

    const closeDecision = () => {
        setShowDecisionModal(false);
        setDecisionComment("");
        setSelectedDemande(null);
        setPendingDecision(null);
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const handlePreview = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}&inline=true`;
        setPreviewDoc({
            url,
            name: path.split("/").pop(),
            path: path
        });
    };

    const closePreview = () => setPreviewDoc(null);

    const handleDownload = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}`;
        window.open(url, "_blank");
    };

    const safeErrorText = (err) => {
        if (!err) return "";
        if (typeof err === "string") return err;
        if (err?.message) return String(err.message);
        try { return JSON.stringify(err); } catch { return String(err); }
    };

    const filtered = getFilteredDemandes();

    if (loading && demandes.length === 0) {
        return (
            <div className="tcPage">
                <div className="tcWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader">
                    <div>
                        <h1>Traitement des demandes de congés</h1>
                        <p>Gérez et validez les demandes de congés et absences de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tnfCard">
                    <div className="tnfControls">
                        <div className="tnfSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, type de congé..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button type="button" className="tnfFilterBtn" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tnfChevron ${showFilters ? "is-open" : ""}`} />
                        </button>

                        <button 
                            type="button" 
                            className="tnfFilterBtn" 
                            style={{ marginLeft: "auto", background: "#10b981", color: "white", border: "none" }}
                            onClick={() => dispatch(exportLeaveRequestsExcelRequest("admin_inbox"))}
                        >
                            <Download size={16} />
                            Exporter Excel
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tnfFilters">
                            <div className="tnfField">
                                <label>Type de congé</label>
                                <select value={filters.typeConge} onChange={(e) => setFilters((p) => ({ ...p, typeConge: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesConges.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tnfField">
                                <label>Date début (à partir du)</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tnfField">
                                <label>Date fin (jusqu'au)</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}

                    {apiError && (
                        <div style={{ marginTop: 10, color: "#ef4444", fontSize: 12 }}>
                            {safeErrorText(apiError)}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tnfCard tnfCard--noPad">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tnfTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Type de congé</th>
                                    <th>{activeTab === "reporte" ? "Nouvelle Période" : "Période"}</th>
                                    {activeTab === "reporte" && <th>Ancienne Période</th>}
                                    <th>Durée</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        onClick={() => showDetails(demande)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>
                                            <span className="tnfPrimary">{demande.matricule}</span>
                                        </td>

                                        <td>
                                            <span className="tnfPrimary">{demande.nom}</span>
                                        </td>

                                        <td>
                                            <div className="tnfType">
                                                <span className="tnfDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <span className="tnfPrimary">{demande.typeConge}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tnfPrimary">{formatDate(demande.dateDebut)}</div>
                                            <div className="tnfSub">→ {formatDate(demande.dateFin)}</div>
                                        </td>

                                        {activeTab === "reporte" && (
                                            <td>
                                                <div className="tnfMuted" style={{ textDecoration: "line-through" }}>{formatDate(demande.originalDateDebut)}</div>
                                                <div className="tnfMuted" style={{ textDecoration: "line-through" }}>→ {formatDate(demande.originalDateFin)}</div>
                                            </td>
                                        )}

                                        <td>
                                            <span className="tnfPrimary">{demande.duree} jour{demande.duree > 1 ? "s" : ""}</span>
                                        </td>

                                        <td>
                                            <span className="tnfMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {demande.motif}
                                            </span>
                                        </td>

                                        <td>{getStatutBadge(demande)}</td>


                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="tnfRowActions">
                                                {activeTab === "enCours" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--ok"
                                                            onClick={() => handleOpenDecision(demande, "approve")}
                                                            title="Approuver"
                                                            disabled={loading}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--no"
                                                            onClick={() => handleOpenDecision(demande, "reject")}
                                                            title="Refuser"
                                                            disabled={loading}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    type="button"
                                                    className="tnfActionBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title="Voir les détails"
                                                    style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tnfModal__sub">Demande du {formatDate(selectedDemande.dateDebut)}</div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfGrid2">
                                {/* Infos collaborateur */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>
                                    <div className="tnfKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.matricule}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Nom</span>
                                        <b>{selectedDemande.nom}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Date de la demande</span>
                                        <b>{formatDate(selectedDemande.dateDemande)}</b>
                                    </div>
                                </div>

                                {/* Détails congé */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>
                                    <div className="tnfKv">
                                        <span>Type</span>
                                        <b>{selectedDemande.typeConge}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{selectedDemande.originalDateDebut ? "Nouvelle Date début" : "Date de début"}</span>
                                        <b className="tnfAccent">{formatDate(selectedDemande.dateDebut)}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{selectedDemande.originalDateFin ? "Nouvelle Date fin" : "Date de fin"}</span>
                                        <b className="tnfAccent">{formatDate(selectedDemande.dateFin)}</b>
                                    </div>
                                    {selectedDemande.originalDateDebut && (
                                        <div className="tnfKv" style={{ background: "#fffbeb", padding: "4px 8px", borderRadius: 6, marginTop: 4 }}>
                                            <span style={{ color: "#92400e" }}>Ancienne période</span>
                                            <b style={{ color: "#92400e", textDecoration: "line-through" }}>
                                                {formatDate(selectedDemande.originalDateDebut)} → {formatDate(selectedDemande.originalDateFin)}
                                            </b>
                                        </div>
                                    )}
                                    <div className="tnfKv">
                                        <span>Durée</span>
                                        <b>{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tnfPanel tnfPanel--full">
                                    <div className="tnfPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tnfText">{selectedDemande.motif}</div>
                                </div>

                                {/* Justificatifs */}
                                {(selectedDemande.justificatif || (selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0)) && (
                                    <div className="tnfPanel tnfPanel--full">
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tnfDocs" style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}>
                                            {/* Support de l'ancien champ (string) */}
                                            {selectedDemande.justificatif && !selectedDemande.justificatifs?.includes(selectedDemande.justificatif) && (
                                                <div className="tnfDoc" onClick={() => handlePreview(selectedDemande.justificatif)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {selectedDemande.justificatif.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#3b82f6" />
                                                </div>
                                            )}
                                            {/* Support du nouveau champ (liste) */}
                                            {selectedDemande.justificatifs?.map((j, idx) => (
                                                <div key={idx} className="tnfDoc" onClick={() => handlePreview(j)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {j.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#3b82f6" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tnfPanel tnfPanel--full">
                                        <div className="tnfPanel__title">
                                            <Check size={16} /> Validation Manager
                                        </div>
                                        <div className="tnfKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tnfKv">
                                                <span>Par</span>
                                                <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tnfKv">
                                                <span>Date</span>
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tnfRejectBox">
                                                <div className="tnfRejectLabel">Commentaire</div>
                                                <div className="tnfRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation RH */}
                                {/*{selectedDemande.statut !== "enCours" && selectedDemande.adminDecision !== null && (*/}
                                {/*    <div className={`tnfPanel tnfPanel--full ${selectedDemande.statut === "approuve" ? "tnfValidOk" : "tnfValidNo"}`}>*/}
                                {/*        <div className="tnfPanel__title">*/}
                                {/*            {selectedDemande.statut === "approuve" ? <Check size={16} /> : <X size={16} />}*/}
                                {/*            {selectedDemande.statut === "approuve" ? " Demande approuvée" : " Demande rejetée"}*/}
                                {/*        </div>*/}
                                {/*        <div className="tnfKv">*/}
                                {/*            <span>Date de validation</span>*/}
                                {/*            <b>{formatDate(selectedDemande.adminDecisionAt)}</b>*/}
                                {/*        </div>*/}
                                {/*        {selectedDemande.adminName && (*/}
                                {/*            <div className="tnfKv">*/}
                                {/*                <span>Validé par</span>*/}
                                {/*                <b>{selectedDemande.adminName}</b>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*        {selectedDemande.adminComment && (*/}
                                {/*            <div className="tnfRejectBox">*/}
                                {/*                <div className="tnfRejectLabel">*/}
                                {/*                    {selectedDemande.statut === "approuve" ? "Commentaire" : "Motif du rejet"}*/}
                                {/*                </div>*/}
                                {/*                <div className="tnfRejectText">{selectedDemande.adminComment}</div>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*    </div>*/}
                                {/*)}*/}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tnfModal__footer">
                            {selectedDemande.statut === "enCours" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "reject");
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "approve");
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DECISION MODAL (Approuver ou Refuser) */}
            {showDecisionModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className={`tnfModal__icon ${pendingDecision === "reject" ? "tnfModal__icon--danger" : ""}`}>
                                    {pendingDecision === "approve" ? <Check size={18} /> : <AlertCircle size={18} />}
                                </div>
                                <div>
                                    <div className="tnfModal__title">
                                        {pendingDecision === "approve" ? "Approuver la demande de congé" : "Refuser la demande de congé"}
                                    </div>
                                    <div className="tnfModal__sub">
                                        {selectedDemande.nom} — {selectedDemande.typeConge}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDecision} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Période</span>
                                    <b>{formatDate(selectedDemande.dateDebut)} → {formatDate(selectedDemande.dateFin)}</b>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Durée</span>
                                    <b className="tnfAccent">{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                </div>
                            </div>

                            <label className="tnfLabel">
                                {pendingDecision === "reject" ? (
                                    <>Motif du refus <span className="tnfReq">*</span></>
                                ) : (
                                    <>Commentaire <span className="tnfMuted">(optionnel)</span></>
                                )}
                            </label>
                            <textarea
                                value={decisionComment}
                                onChange={(e) => setDecisionComment(e.target.value)}
                                rows={5}
                                placeholder={
                                    pendingDecision === "reject"
                                        ? "Veuillez préciser le motif du refus de cette demande..."
                                        : "Ajouter un commentaire (optionnel)..."
                                }
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">Ce commentaire sera communiqué au collaborateur</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDecision}>
                                Annuler
                            </button>

                            {pendingDecision === "approve" ? (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--ok"
                                    onClick={confirmDecision}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer l'approbation"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--danger"
                                    onClick={confirmDecision}
                                    disabled={!decisionComment.trim() || loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* PREVIEW MODAL */}
            {previewDoc && (
                <div className="tnfModal" style={{ zIndex: 10001 }}>
                    <div className="tnfModal__panel tnfModal__panel--wide" style={{ maxWidth: "90vw", width: "1000px" }}>
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon"><FileText size={18} /></div>
                                <div>
                                    <div className="tnfModal__title">Aperçu du document</div>
                                    <div className="tnfModal__sub">{previewDoc.name}</div>
                                </div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={closePreview}><X size={18} /></button>
                        </div>
                        <div className="tnfModal__body" style={{ height: "75vh", padding: 0, overflow: "hidden", background: "#f1f5f9", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {previewDoc.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                                    <img src={previewDoc.url} alt="Aperçu" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)" }} />
                                </div>
                            ) : previewDoc.name.match(/\.pdf$/i) ? (
                                <iframe src={previewDoc.url} title="Aperçu PDF" width="100%" height="100%" style={{ border: "none" }} />
                            ) : (
                                <div style={{ textAlign: "center", padding: 40 }}>
                                    <AlertCircle size={48} color="#64748b" style={{ marginBottom: 16 }} />
                                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 8 }}>Aperçu non disponible</div>
                                    <p style={{ color: "#64748b", marginBottom: 24 }}>Ce type de fichier ne peut pas être prévisualisé directement.</p>
                                    <button className="tnfBtn tnfBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                        <Download size={16} /> Télécharger pour consulter
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="tnfModal__footer">
                            <button className="tnfBtn tnfBtn--ghost" onClick={closePreview}>Fermer</button>
                            <button className="tnfBtn tnfBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                <Download size={16} /> Télécharger le document
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementConges;
